import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cargarGoogleMaps } from '../utils/googleMaps'
import { matchProvincia, matchLocalidad } from '../data/argentina'
import { colors, font, text as tipo } from '../design/tokens'

/*
 * Campo de dirección con autocompletado de Google Places (Argentina).
 *
 * - Renderiza NUESTRO input estilado (mismo `inputStyle` que el resto de los
 *   forms, igual que CampoLocalidad #26). Es un input controlado por
 *   `value`/`onChange` → funciona como FALLBACK si Google no carga.
 * - Al tipear (3+ caracteres) le pide sugerencias a Google y dibuja NUESTRA
 *   lista. Al elegir una, parsea los componentes y llama:
 *     onPlaceSelected({ calle, localidad, provincia, codigo_postal, lat, lng })
 *   con provincia/localidad ya canónicas (matchProvincia / matchLocalidad).
 * - Degradación elegante: si cargarGoogleMaps() devuelve null o falla, queda
 *   como un <input> normal — el form sigue andando sin Google.
 *
 * API NUEVA de Places (AutocompleteSuggestion), migrado en ago 2026:
 *   el widget viejo (google.maps.places.Autocomplete) quedó deprecado — Google
 *   no lo da a clientes nuevos desde el 1/3/2025 y avisa que lo discontinúa con
 *   12 meses de aviso. Se migró a la API nueva PIDIENDO los datos y dibujando
 *   la lista nosotros, en vez de usar el web component PlaceAutocompleteElement:
 *   ese trae su propia UI y no se puede estilar como el resto de los campos.
 *
 * Diferencias del contrato nuevo que costaron probar contra Google en vivo:
 *   - los componentes vienen como `longText`/`shortText` (antes long_name/short_name)
 *   - `includedPrimaryTypes` NO acepta la colección 'address' del widget viejo:
 *     hay que enumerar los tipos reales (street_address, premise, subpremise, route)
 *   - el sessionToken se descarta después de pedir los detalles: esa llamada
 *     CIERRA la sesión de facturación y la próxima búsqueda abre una nueva.
 */

// Tipos de lugar que nos interesan: direcciones, no negocios ni ciudades.
const TIPOS_DIRECCION = ['street_address', 'premise', 'subpremise', 'route']
// Debajo de esto no vale la pena consultar (ni gastar) — son casi todas las calles.
const MIN_CARACTERES = 3
// Espera a que el usuario deje de tipear: 1 consulta por pausa, no 1 por tecla.
const ESPERA_MS = 250

/**
 * Devuelve lo que el operador escribió DESPUÉS de la altura.
 *
 * En Argentina la dirección se escribe calle + altura + piso/depto, así que
 * todo lo que viene después del número es el detalle que Google no conoce
 * ("6° B", "PB", "timbre 4", "casa del fondo"). Se busca la ÚLTIMA aparición de
 * la altura para no cortar en un número que forme parte del nombre de la calle
 * (caso real: "11 de Septiembre 1530").
 */
function detalleTrasAltura(texto, altura) {
  const t = String(texto || '')
  const i = t.lastIndexOf(String(altura))
  if (i < 0) return ''
  return t.slice(i + String(altura).length).replace(/^[\s,.-]+/, '').trim()
}

// Saca el componente que matchea alguno de los tipos pedidos.
function comp(components, ...tipos) {
  for (const t of tipos) {
    const c = components.find(x => x.types?.includes(t))
    if (c) return c
  }
  return null
}

export default function CampoDireccion({
  value,
  onChange,
  onPlaceSelected,
  inputStyle,
  placeholder = 'Calle y número',
  required = false,
}) {
  const inputRef = useRef(null)
  // Clases de la API nueva, una vez cargadas. Mientras sea null → input normal.
  const apiRef = useRef(null)
  // Token de sesión de Google: agrupa las tecleadas + el pedido de detalles en
  // UNA sola facturación. Se descarta al pedir detalles (ahí cierra la sesión).
  const tokenRef = useRef(null)
  const temporizadorRef = useRef(null)
  // Contador para descartar respuestas que llegan tarde (fuera de orden).
  const secuenciaRef = useRef(0)

  // Los callbacks pueden cambiar entre renders; van a un ref para que los
  // handlers async siempre llamen a la versión actual sin re-enganchar nada.
  const onSelectRef = useRef(onPlaceSelected)
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onSelectRef.current = onPlaceSelected
    onChangeRef.current = onChange
  }, [onPlaceSelected, onChange])

  const [sugerencias, setSugerencias] = useState([])
  const [abierto, setAbierto] = useState(false)
  const [resaltado, setResaltado] = useState(-1)
  const [pos, setPos] = useState(null)
  const listaId = useId()

  // --- carga de la librería ------------------------------------------------
  useEffect(() => {
    let vivo = true
    cargarGoogleMaps().then(async (maps) => {
      if (!vivo || !maps?.importLibrary) return // → sigue como input normal
      try {
        const { AutocompleteSuggestion, AutocompleteSessionToken } =
          await maps.importLibrary('places')
        if (vivo) apiRef.current = { AutocompleteSuggestion, AutocompleteSessionToken }
      } catch {
        /* sin autocompletado, pero el campo sigue usable */
      }
    })
    return () => {
      vivo = false
      clearTimeout(temporizadorRef.current)
    }
  }, [])

  // --- posición de la lista ------------------------------------------------
  // Va en un portal al <body> porque los modales de LGR tienen overflow:auto y
  // recortarían una lista posicionada adentro (por eso Google montaba la suya
  // en el body). Se recalcula al scrollear/redimensionar mientras está abierta.
  const recalcularPos = useCallback(() => {
    const el = inputRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setPos({ left: r.left, top: r.bottom + 2, width: r.width })
  }, [])

  useEffect(() => {
    if (!abierto) return
    recalcularPos()
    window.addEventListener('scroll', recalcularPos, true)
    window.addEventListener('resize', recalcularPos)
    return () => {
      window.removeEventListener('scroll', recalcularPos, true)
      window.removeEventListener('resize', recalcularPos)
    }
  }, [abierto, recalcularPos])

  // --- consulta a Google ---------------------------------------------------
  const buscar = useCallback(async (texto) => {
    const api = apiRef.current
    if (!api) return
    if (!tokenRef.current) tokenRef.current = new api.AutocompleteSessionToken()

    const mia = ++secuenciaRef.current
    try {
      const { suggestions } = await api.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: texto,
        includedRegionCodes: ['ar'],
        includedPrimaryTypes: TIPOS_DIRECCION,
        language: 'es',
        region: 'AR',
        sessionToken: tokenRef.current,
      })
      if (mia !== secuenciaRef.current) return // respuesta vieja: la ignoramos

      const items = (suggestions || [])
        .map(s => s.placePrediction)
        .filter(Boolean)
        .map(p => ({
          id: p.placeId,
          principal: p.mainText?.text || p.text?.text || '',
          secundario: p.secondaryText?.text || '',
          prediccion: p,
        }))
      setSugerencias(items)
      setResaltado(-1)
      setAbierto(items.length > 0)
    } catch {
      if (mia !== secuenciaRef.current) return
      setSugerencias([])
      setAbierto(false)
    }
  }, [])

  // --- elección de una sugerencia -----------------------------------------
  const elegir = useCallback(async (item) => {
    setAbierto(false)
    setSugerencias([])
    setResaltado(-1)
    secuenciaRef.current++ // descarta cualquier búsqueda en vuelo

    try {
      const place = item.prediccion.toPlace()
      await place.fetchFields({ fields: ['addressComponents', 'location'] })
      // Pedir detalles CIERRA la sesión de facturación: la próxima búsqueda
      // tiene que abrir un token nuevo.
      tokenRef.current = null

      const components = place.addressComponents || []
      if (!components.length) return

      const altura = comp(components, 'street_number')?.longText || ''
      const calleSola = comp(components, 'route')?.longText || ''
      const calle = [calleSola, altura].filter(Boolean).join(' ').trim()

      // Lo que el operador escribió DESPUÉS de la altura y Google no devuelve:
      // piso, depto, timbre, "casa del fondo". Google sólo conoce calle+altura,
      // así que al completar el campo con su respuesta ese detalle se perdía en
      // silencio — el remito salía a "Melo 1530" cuando el pedido iba al 6° B, y
      // el repartidor llegaba al edificio sin saber a qué puerta tocar.
      // Se lee del DOM: es el texto que el operador tiene tipeado en ese
      // momento, sin depender de que el estado del padre haya llegado.
      const extra = altura ? detalleTrasAltura(inputRef.current?.value, altura) : ''

      const localidadRaw =
        comp(components, 'locality', 'sublocality', 'administrative_area_level_2')?.longText || ''
      const provinciaRaw =
        comp(components, 'administrative_area_level_1')?.longText || ''
      // Google devuelve el CP en formato CPA (ej. "C1023"). Lo normalizamos a
      // solo dígitos (→ "1023") para que quede igual que la base actual y que la
      // auto-asignación de zona (que stripea no-dígitos) lo trate consistente.
      const codigoPostal = (comp(components, 'postal_code')?.longText || '').replace(/\D/g, '')

      const loc = place.location
      const lat = loc ? loc.lat() : null
      const lng = loc ? loc.lng() : null

      // CABA llega como "Ciudad Autónoma de Buenos Aires" → matchProvincia lo
      // mapea a 'CAPITAL FEDERAL'. matchLocalidad deja la localidad canónica.
      const datos = {
        calle: calle || calleSola || '',
        // El renglón COMPLETO: calle+altura de Google + el piso/depto/timbre
        // que el operador tipeó. Es lo que deben guardar los forms de campo
        // único ("Domicilio (calle, número, piso, depto)") — usar `calle` solo
        // ahí BORRA el piso al elegir la sugerencia. Los forms con campo Piso
        // propio (NV) siguen usando `calle` + `detalle_extra` por separado.
        calle_completa: [calle || calleSola, extra].filter(Boolean).join(' ').trim(),
        localidad: matchLocalidad(localidadRaw),
        provincia: matchProvincia(provinciaRaw),
        codigo_postal: codigoPostal,
        // Piso/depto/timbre que Google no devuelve pero el operador sí escribió.
        detalle_extra: extra,
        lat,
        lng,
      }

      // El input queda con el renglón completo (con el piso que se tipeó);
      // si el form guarda otra cosa en su estado, la pisa en onPlaceSelected.
      const texto = datos.calle_completa || datos.calle
      if (texto) onChangeRef.current?.(texto)
      onSelectRef.current?.(datos)
    } catch {
      /* si falla el detalle, el texto tipeado por el usuario queda como está */
    }
  }, [])

  // --- interacción ---------------------------------------------------------
  const alTipear = (v) => {
    onChange?.(v)
    clearTimeout(temporizadorRef.current)
    if (!apiRef.current || v.trim().length < MIN_CARACTERES) {
      secuenciaRef.current++
      setSugerencias([])
      setAbierto(false)
      return
    }
    temporizadorRef.current = setTimeout(() => buscar(v.trim()), ESPERA_MS)
  }

  const alTeclear = (e) => {
    // Enter NUNCA envía el form desde este campo (se mantiene el comportamiento
    // anterior); si hay una sugerencia resaltada, la elige.
    if (e.key === 'Enter') {
      e.preventDefault()
      if (abierto && resaltado >= 0 && sugerencias[resaltado]) elegir(sugerencias[resaltado])
      return
    }
    if (!abierto || !sugerencias.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setResaltado(i => (i + 1) % sugerencias.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setResaltado(i => (i <= 0 ? sugerencias.length - 1 : i - 1))
    } else if (e.key === 'Escape') {
      setAbierto(false)
    }
  }

  const lista = abierto && pos && sugerencias.length > 0 && typeof document !== 'undefined'
    ? createPortal(
        <ul id={listaId} role="listbox" style={{ ...estilos.lista, left: pos.left, top: pos.top, width: pos.width }}>
          {sugerencias.map((s, i) => (
            <li
              key={s.id || i}
              role="option"
              aria-selected={i === resaltado}
              style={{ ...estilos.opcion, background: i === resaltado ? colors.bgSoft : colors.bg }}
              // preventDefault en mousedown: evita que el input pierda el foco
              // ANTES del click, que es lo que hacía "desaparecer" la opción.
              onMouseDown={e => e.preventDefault()}
              onMouseEnter={() => setResaltado(i)}
              onClick={() => elegir(s)}
            >
              <span style={estilos.principal}>{s.principal}</span>
              {s.secundario && <span style={estilos.secundario}>{s.secundario}</span>}
            </li>
          ))}
        </ul>,
        document.body,
      )
    : null

  return (
    <>
      <input
        ref={inputRef}
        style={inputStyle}
        value={value || ''}
        required={required}
        placeholder={placeholder}
        onChange={e => alTipear(e.target.value)}
        onKeyDown={alTeclear}
        onBlur={() => setAbierto(false)}
        onFocus={() => { if (sugerencias.length) setAbierto(true) }}
        autoComplete="off"
        role="combobox"
        aria-expanded={abierto}
        aria-controls={listaId}
        aria-autocomplete="list"
      />
      {lista}
    </>
  )
}

const estilos = {
  lista: {
    position: 'fixed',
    zIndex: 100000, // por encima de los modales (overlay usa 1000)
    margin: 0,
    padding: '4px 0',
    listStyle: 'none',
    background: colors.bg,
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    height: 260,
    overflowY: 'auto',
    fontFamily: font.sans,
  },
  opcion: {
    padding: '7px 10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  principal: {
    fontSize: tipo.body,
    color: colors.text,
  },
  secundario: {
    fontSize: tipo.meta,
    color: colors.textSec,
  },
}
